"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { MousePointer2, Plus, CreditCard } from "lucide-react";

const STEPS = [
  {
    icon: MousePointer2,
    title: { en: "Pick", es: "Elige" },
    sub: { en: "Big photo, big buttons. Hungry-people-friendly.", es: "Foto grande, botones grandes. Friendly para gente con hambre." },
  },
  {
    icon: Plus,
    title: { en: "Customize", es: "Customiza" },
    sub: { en: "Modifiers in a bottom sheet. Required first, optional second.", es: "Modificadores en bottom sheet. Requeridos primero, opcionales después." },
  },
  {
    icon: CreditCard,
    title: { en: "Pay", es: "Paga" },
    sub: { en: "Tap or chip on the card terminal — Apple Pay works too. Or pay at the counter.", es: "Tap o chip en la terminal — Apple Pay también. O paga en el mostrador." },
  },
];

export default function KioskOrderFlowScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#FAFAF8] overflow-hidden">
      <div
        className="absolute top-1/4 right-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.10) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Customer flow", "Flujo del cliente")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Pick. Customize. ", "Elige. Customiza. ")}
            <span className="text-redy-charcoal/30">{t("Pay.", "Paga.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "A few taps to a paid order. Just a name for the order — plus a phone if they want a text when it's ready. No account, no forced signup. Get them through, get them fed.",
              "Pocos taps a una orden pagada. Solo un nombre para la orden — y un teléfono si quieren un SMS cuando esté lista. Sin cuenta, sin registro obligado. Pásalos, dales de comer."
            )}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-[1080px] mx-auto">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title.en}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: 0.1 * i, ease: [0.21, 0.47, 0.32, 0.98] }}
                className="bg-white border border-black/5 rounded-[20px] p-6 shadow-[0_20px_60px_rgba(0,0,0,0.04)]"
              >
                <div
                  className="w-11 h-11 rounded-[12px] flex items-center justify-center mb-5"
                  style={{ backgroundColor: "rgba(244,114,43,0.12)" }}
                >
                  <Icon className="w-5 h-5" style={{ color: "#F4722B" }} />
                </div>
                <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-1">
                  {t("Step", "Paso")} {i + 1}
                </div>
                <div className="text-[22px] font-black text-redy-charcoal mb-3 tracking-tight">
                  {t(s.title.en, s.title.es)}
                </div>
                <div className="text-[13px] font-medium text-redy-secondary leading-relaxed">
                  {t(s.sub.en, s.sub.es)}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
